import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from 'react-hook-form';
import { DocumentsRenderer } from "../custom/forms/documents-renderer";
import { useDispatch } from 'react-redux';
import { fetchUploadedDocuments, fetchDocumentRequirements } from "../../stores/async/async-actions";
import { useEntityState, useSelectedEntity } from "../../stores/entity/entity.hooks";
import NavButton from "../portico/button/button";
import Spacing from "../portico/spacing/spacing";
import CONSTANTS from "../../utils/constants";
import { EntityDraftViewModel } from "../../interfaces/EntityDraftViewModel";
import Button from "@material-ui/core/Button";
import DataPolicyTaskDetailsStyles from "./data-policy-task-details.styles";
import { decreaseCurrentStepperIndex, increaseCurrentStepperIndex, setDocumentUploadBack, setSnackBar } from "../../stores/ui/ui-action";
import { useDocumentUploadBack } from "../../stores/ui/ui.hooks";
import CustomLoading from "../custom/custom-loading";
import { FastRewindTwoTone } from "@material-ui/icons";
import { uploadDocument } from "../../services/bff-service";
import { parseClassificationAttribute } from "../custom/forms/form-helper-methods";

interface DocumentPolicyTaskDetailsProps {
  readOnly: boolean,
  journeyId: string,
  entityDraft: EntityDraftViewModel,
  currentTask: object,
  completeTask: () => Promise<void>,
  handleBackClick: Function,
  deleteRequired: boolean,
}

export const DocumentPolicyTaskDetails: React.FC<DocumentPolicyTaskDetailsProps> = ({ readOnly, entityDraft, completeTask, handleBackClick, deleteRequired, currentTask }) => {
  const {register, formState: {errors}, handleSubmit, trigger, setValue, clearErrors}: any = useForm();
  const [files, setFiles]=useState([]);

  const { journeyId } = useParams<{
    journeyId: string;
  }>();
  const { documentRequirementsLoadInProgress, documentRequirements, documentsLoadInProgress, documents } = useEntityState();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [uploadCancel, setUploadCancel] = useState<boolean>(false);
  const dispatch = useDispatch();
  const classes = DataPolicyTaskDetailsStyles();
  const documentUploadBack = useDocumentUploadBack();
  const selectedEntity = useSelectedEntity();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  useEffect(() => {
    if (!documentRequirementsLoadInProgress && (!documentRequirements || documentUploadBack)) {
      dispatch(fetchDocumentRequirements(entityDraft, currentTask));
      dispatch(setDocumentUploadBack(false));
    }

    if ((!documentsLoadInProgress && !documents) || uploadCancel) {
      dispatch(fetchUploadedDocuments(journeyId, selectedEntity?.id));
    }
  }, [entityDraft, documentRequirementsLoadInProgress, documentRequirements, documentsLoadInProgress, documents, dispatch, uploadCancel]);

  let classification = [];
  documentRequirements && documentRequirements.forEach(document => {
    if (document.classification) {
      classification.push(document.classification);
    }
  })

  const uiMetaData: any = parseClassificationAttribute(classification[0]);

  const onBackClick = () => {
    dispatch(decreaseCurrentStepperIndex());
    dispatch(setDocumentUploadBack(true));
    handleBackClick();
  }
  
  const onSaveClicked = async () => {
    setSubmitting(true);
    await completeTask();
    dispatch(increaseCurrentStepperIndex())
    setSubmitting(false);
  };

  const enableSaveButton = () => {
    var enableButton = true;
    var ignorableDocuments = ['otherDocuments']
    
    documentRequirements.filter(doc=>!ignorableDocuments.includes(doc['documentDataKey'])).forEach(document => {
      var check = false;
      documents.forEach(uploadedDocument => {
        if (uploadedDocument['documentDataKey'] && uploadedDocument['documentDataKey'] === document['documentDataKey']) {
          check = true;
        }
      })
      if (!check) {
        enableButton = false;
      }
    })
    return enableButton;
  }

  const onClickDisabledButton = async () => {
    setIsSubmit(true);
    if (!enableSaveButton()) {
      const validations = await Promise.all(
        documentRequirements.map(async(_, index) => await trigger(`file${index}`))
      )
      
      clearFieldErrors();
    }
  }

  const clearFieldErrors = () => {
    const datakeys = getDocumentDataKeys();

    documentRequirements.map((doc, index) => {
      if(datakeys.indexOf(doc['documentDataKey']) > -1 || doc['isMandatory'] == false) {
        clearErrors(`file${index}`);
      }
    });
  }

  const getDocumentDataKeys = () => {
    return documents.map(doc => doc['documentDataKey']);
  }

  const onFileChange = async (event, index) => {
    const temp = files;
    temp[index] = event.target?.files[0]
    setFiles(temp[index])
    setValue(`file${index}`,event.target.files)
    await Promise.all(
      documentRequirements.map(async(_, index) => await trigger(`file${index}`))
    )
    clearFieldErrors();
  };

  return (
    <>
      {/* <LoadingOnBackdrop status={submitting} message="Saving data" /> */}
      {submitting && <CustomLoading message="Saving Data..." />}
      {(!entityDraft || documentRequirementsLoadInProgress || documentsLoadInProgress)}

      {entityDraft && documentRequirements &&
        (
          <div className="MuiPaper-elevation1">
            <div className={classes.container}>
              <div><h1 className="MuiTypography-h1" style={{ margin: '40px 0 40px' }}>Please upload the following documents</h1></div>
              <DocumentsRenderer
                dataSource={documentRequirements}
                journeyId={journeyId}
                journeyDocuments={documents}
                metaData={uiMetaData}
                loadingDocuments={documentsLoadInProgress}
                entityId={entityDraft["id"]}
                deleteRequired={deleteRequired}
                setUploadCancel={setUploadCancel}
                register={register}
                onChange={onFileChange}
                error={isSubmit ? errors : ''}
                name="document-policy-task-details"
              />
            </div>
          </div>
        )
      }
      <Spacing variable="spacing-base-x3" />
      {entityDraft && documentRequirements && documents && (
        <>
          <div className={classes.buttoncontainer}>
            {<div className={classes.button}><Button className={classes.backButton} onClick={onBackClick} variant="outlined">Back</Button></div>}
            <div className={classes.button} onClick={onClickDisabledButton}><NavButton onNext={onSaveClicked} disableButton={!enableSaveButton()} dataCy='save-btn' buttonLabel={CONSTANTS.SAVE_CONTINUE} /></div>
          </div>
        </>

      )}
    </>
  );
};